import React, { useState } from 'react';
import { formatCurrency } from '../../utils/formatters';
import SafeIcon from '../../common/SafeIcon';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import FinancialReportSection, {
  FinancialReportRow
} from './FinancialReportSection';

const formatDate = (value) => {
  if (!value) return '—';

  return new Date(`${value}T00:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const DetailAccount = ({ account, onDrillDown }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center gap-3 px-6 py-3 text-left transition-colors hover:bg-blue-50/40"
      >
        <SafeIcon
          icon={open ? FiChevronDown : FiChevronRight}
          className="text-slate-400"
        />
        <span className="flex-1 text-sm font-normal text-slate-700">
          {account.code ? `${account.code} · ` : ''}{account.name}
        </span>
        <span className="text-sm font-semibold tabular-nums text-slate-900">
          {formatCurrency(account.amount)}
        </span>
      </button>

      {open && (
        <div className="mx-6 mb-4 overflow-x-auto rounded-lg border border-slate-200 bg-slate-50/70">
          <table className="min-w-full text-left">
            <thead>
              <tr className="border-b border-slate-200 text-[10px] font-medium uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Reference</th>
                <th className="px-4 py-3 text-right">Debit</th>
                <th className="px-4 py-3 text-right">Credit</th>
                <th className="px-4 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(account.transactions || []).map((transaction) => (
                <tr
                  key={transaction.id}
                  onClick={() => onDrillDown?.(account.name)}
                  className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-white"
                >
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-600">
                    {formatDate(transaction.date)}
                  </td>
                  <td className="min-w-[180px] px-4 py-3 text-xs text-slate-700">
                    {transaction.description || '—'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-slate-600">
                    {transaction.reference || '—'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-xs tabular-nums text-slate-600">
                    {formatCurrency(transaction.debit)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-xs tabular-nums text-slate-600">
                    {formatCurrency(transaction.credit)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-xs font-semibold tabular-nums text-slate-900">
                    {formatCurrency(transaction.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const AccountBalance = ({ account }) => (
  <FinancialReportRow
    label={account.code ? `${account.code} · ${account.name}` : account.name}
    value={account.amount}
  />
);

const DetailSection = ({ title, accounts, total }) => (
  <FinancialReportSection
    title={title}
    totalLabel={`Total ${title}`}
    total={total}
    emptyLabel="No active accounts in this section."
    rows={
      accounts.length > 0 && (
        <>
          {accounts.map((account) => (
            <AccountBalance key={account.id} account={account} />
          ))}
        </>
      )
    }
  />
);

export default function ProfitLossDetails({
  reportData,
  showZeroBalances,
  onToggleZeroBalances,
  onDrillDown
}) {
  const sections = [
    {
      title: 'Income',
      key: 'income',
      accounts: reportData.accountDetails.income,
      total: reportData.income.total
    },
    {
      title: 'Cost of Goods Sold',
      key: 'costOfGoodsSold',
      accounts: reportData.accountDetails.costOfGoodsSold,
      total: reportData.costOfGoodsSold.total
    },
    {
      title: 'Operating Expenses',
      key: 'expenses',
      accounts: reportData.accountDetails.expenses,
      total: reportData.expenses.total
    }
  ];

  void DetailAccount;
  void onDrillDown;

  return (
    <div>
      <div className="mb-5 flex flex-col gap-3 border-b border-slate-200 pb-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="text-sm font-medium text-slate-900">
            Account Details
          </h3>
          <p className="mt-1 text-xs text-slate-500">
            Account-level balances from posted transactions in the selected period.
          </p>
        </div>

        <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-700">
          <input
            type="checkbox"
            checked={showZeroBalances}
            onChange={(event) => onToggleZeroBalances(event.target.checked)}
            className="h-4 w-4 rounded border-slate-300 text-blue-600"
          />
          Show Zero Balance Accounts
        </label>
      </div>

      {sections.map((section) => (
        <DetailSection
          key={section.key}
          title={section.title}
          accounts={section.accounts.filter((account) => (
            showZeroBalances || Number(account.amount || 0) !== 0
          ))}
          total={section.total}
        />
      ))}

      <FinancialReportRow
        label="Net Profit (Loss)"
        value={reportData.netIncome}
        total
        grand
      />
    </div>
  );
}